import React, { useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { set } from "../../../app/globalSlice";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { getLocaleDataGrid } from "../../../helpers/GetLanguage";
import { objectToQuery, requestApi } from "../../../helpers/RequestApi";
import { GET, PATCH } from "../../../utils/MethodUtils";
import { SAV_SERVICE_TODO_URL } from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import { Box, Container, Link } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid/models/gridSortModel";
import { searchParamToObject } from "../../../helpers/SearchParamHelper";
import { useSearchParams } from "react-router-dom";
import { SavServiceTodoInterface } from "../../../interfaces/SavInterface";
import CopyClipboardComponent from "../CopyClipboardComponent";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import getErrorApi from "../../../helpers/GetErrorApi";
import { LoadingButton } from "@mui/lab";
import FilterSearchComponent from "../filter/FilterSearchComponent";
import FDocenteteConsoleComptoirComponent from "../console/FDocenteteConsoleComptoirComponent";
import DrawerComponent from "../DrawerComponent";
import { useContainerDimensions } from "../useContainerDimensions";

export interface State {
  savServiceTodo: SavServiceTodoInterface;
}

const PointageComponent: React.FC<State> = React.memo(({ savServiceTodo }) => {
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const [realIsPointage, setRealIsPointage] = React.useState<boolean>(
    savServiceTodo.done
  );
  const [loading, setLoading] = React.useState<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const nbSavServiceTodo = useAppSelector(
    (state: RootState) => state.globalState.nbSavServiceTodo
  );

  const handleChange = React.useCallback(async () => {
    setLoading(true);
    const response = await requestApi({
      method: PATCH,
      path: SAV_SERVICE_TODO_URL + "/" + savServiceTodo.id,
      allowError: false,
      token: token,
      body: {
        done: !realIsPointage,
      },
    });
    if (response.statusCode === 200) {
      setRealIsPointage(response.content.done);
      dispatch(
        set({
          nbSavServiceTodo: response.content.done
            ? (nbSavServiceTodo ?? 1) - 1
            : (nbSavServiceTodo ?? 0) + 1,
        })
      );
    } else if (response.statusCode === 401) {
      toastr.info(t("word.info"), t("error.reconnect"));
    } else {
      for (let message of getErrorApi(response.content)) {
        toastr.error(t("word.error"), t(message));
      }
    }
    setLoading(false);
  }, [savServiceTodo.id, token, realIsPointage, dispatch, nbSavServiceTodo, t]);

  return (
    <>
      <LoadingButton
        variant="text"
        color="inherit"
        sx={{
          borderRadius: "50%",
          minWidth: "auto",
          padding: "12px",
        }}
        loading={loading}
        onClick={handleChange}
      >
        {realIsPointage ? (
          <DoneIcon
            color={savServiceTodo.done !== realIsPointage ? "info" : "success"}
          />
        ) : (
          <CloseIcon
            color={savServiceTodo.done !== realIsPointage ? "info" : "error"}
          />
        )}
      </LoadingButton>
    </>
  );
});

const minWidth = 1500;

const SavPrestationComponent: React.FC = React.memo(() => {
  const refreshPage = useAppSelector(
    (state: RootState) => state.globalState.refreshPage
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const componentRef = useRef();
  const { width } = useContainerDimensions(componentRef);
  const dispatch = useAppDispatch();
  const [init, setInit] = React.useState(false);
  const [defaultItemsPerPage] = React.useState(50);
  const [canApplyFilter, setCanApplyFilter] = React.useState(false);
  const [savServiceTodos, setSavServiceTodos] = React.useState<
    SavServiceTodoInterface[] | undefined
  >(undefined);
  const [totalItems, setTotalItems] = React.useState(0);
  const [openDrawer, setOpenDrawer] = React.useState("");
  const { t, i18n } = useTranslation();
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const user = useAppSelector((state: RootState) => state.globalState.user);
  const handleOpen = React.useCallback((doPiece: string) => {
    setOpenDrawer(doPiece);
  }, []);
  const handleClose = React.useCallback(() => {
    setOpenDrawer("");
  }, []);
  const getColumns = React.useCallback((): GridColDef[] => {
    return [
      {
        field: "id",
        headerName: t("column.id"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
      },
      {
        field: "userIdentifier",
        headerName: t("column.userIdentifier"),
        flex: 1,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CopyClipboardComponent
            className="RobotoMono"
            component="span"
            text={params.row.userIdentifier ?? ""}
          />
        ),
      },
      {
        field: "fDocenteteId",
        headerName: t("column.fDocenteteId"),
        flex: 1,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Link
            onClick={() => handleOpen(params.row.fDocenteteId)}
            sx={{ cursor: "pointer" }}
          >
            {params.row.fDocenteteId}
          </Link>
        ),
      },
      {
        field: "done",
        headerName: t("column.done"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <PointageComponent savServiceTodo={params.row} />
        ),
      },
      {
        field: "created",
        headerName: t("column.created"),
        flex: 1,
        headerClassName: "background-nove",
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) =>
          new Date(params.row.created).toLocaleString(i18n.language, {
            dateStyle: "long",
            timeStyle: "medium",
            timeZone: "America/Cuiaba",
          }),
      },
    ];
  }, [handleOpen, i18n.language, t]);
  const [columns, setColumns] = React.useState<GridColDef[]>(getColumns());
  const load = React.useCallback(
    async (force: boolean = false) => {
      setInit(true);
      if (!user || (savServiceTodos !== undefined && !force)) {
        dispatch(set({ refreshPage: false }));
        return;
      }
      const searchParamsObject = searchParamToObject(searchParams);
      let hasChanged = false;
      if (
        !searchParamsObject.hasOwnProperty("page") ||
        searchParamsObject.page === "undefined"
      ) {
        searchParamsObject.page = 1;
        hasChanged = true;
      }
      if (!searchParamsObject.hasOwnProperty("itemsPerPage")) {
        searchParamsObject.itemsPerPage = defaultItemsPerPage;
        hasChanged = true;
      }
      if (!force) {
        let hasOrder = false;
        for (const [key] of Object.entries(searchParamsObject)) {
          if (key.startsWith("order")) {
            hasOrder = true;
            break;
          }
        }
        if (!hasOrder) {
          searchParamsObject["order[id]"] = "desc";
          hasChanged = true;
        }
      }
      if (hasChanged) {
        dispatch(set({ refreshPage: false }));
        setSearchParams(searchParamsObject, {
          replace: true,
        });
        return;
      }
      const response = await requestApi({
        method: GET,
        path: SAV_SERVICE_TODO_URL + objectToQuery(searchParamsObject),
        allowError: false,
        token: token,
        paginate: true,
      });
      if (response.statusCode === 200) {
        let maxId = Math.max(
          ...response.content["hydra:member"].map(
            (o: SavServiceTodoInterface) => o.id
          )
        );
        const newSavServiceTodos = [
          ...Array.from(
            Array(
              Number(searchParamsObject.itemsPerPage) *
                (Number(searchParamsObject.page) - 1)
            )
          ).map(() => {
            maxId++;
            return {
              id: maxId,
            };
          }),
          ...response.content["hydra:member"],
        ];
        setSavServiceTodos(newSavServiceTodos);
        setTotalItems(response.content["hydra:totalItems"]);
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        toastr.error(t("word.error"), t("error.tryAgain"));
      }
      setCanApplyFilter(true);
      dispatch(set({ refreshPage: false }));
    },
    [
      defaultItemsPerPage,
      dispatch,
      savServiceTodos,
      searchParams,
      setSearchParams,
      t,
      token,
      user,
    ]
  );

  const onPageSizeChange = React.useCallback(
    (pageSize: number) => {
      if (!init) {
        return;
      }
      setSavServiceTodos(undefined);
      const searchParamsObject = searchParamToObject(searchParams);
      searchParamsObject.itemsPerPage = pageSize;
      searchParamsObject.page = 1;
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const onPageChange = React.useCallback(
    (page: number) => {
      if (!init) {
        return;
      }
      setSavServiceTodos(undefined);
      const searchParamsObject = searchParamToObject(searchParams);
      searchParamsObject.page = page + 1;
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const onSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      if (!init) {
        return;
      }
      const searchParamsObject = searchParamToObject(searchParams);
      for (const [key] of Object.entries(searchParamsObject)) {
        if (key.startsWith("order")) {
          delete searchParamsObject[key];
        }
      }
      for (const sort of model) {
        // @ts-ignore
        searchParamsObject["order[" + sort.field + "]"] = sort.sort;
      }
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const getSortModel = React.useCallback(() => {
    const searchParamsObject = searchParamToObject(searchParams);
    const result: any[] = [];
    for (const [key, value] of Object.entries(searchParamsObject)) {
      if (key.startsWith("order")) {
        let field: string[] | string = key.split("[");
        field = field[1].replace("]", "");
        result.push({
          field: field,
          sort: value,
        });
      }
    }
    return result;
  }, [searchParams]);

  React.useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (init && refreshPage) {
      load(true);
    }
  }, [refreshPage]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setColumns(getColumns());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!user) {
      setSearchParams(
        {},
        {
          replace: true,
        }
      );
    }
    if (init && !refreshPage) {
      dispatch(set({ refreshPage: true }));
    }
  }, [searchParams, user?.userIdentifier]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchParamsObject = searchParamToObject(searchParams);
  return (
    <>
      <DrawerComponent
        open={openDrawer !== ""}
        onClose={handleClose}
        handleDrawerClose={handleClose}
        drawerwidth={width < minWidth ? width : minWidth}
        content={
          <FDocenteteConsoleComptoirComponent
            doPiece={openDrawer}
            handleClose={handleClose}
          />
        }
      />
      <Box ref={componentRef}>
        <Container sx={{ marginY: 2 }}>
          <FilterSearchComponent
            init={canApplyFilter}
            placeholder="word.searchSavPlaceholder"
          />
          {init && searchParamsObject.itemsPerPage && searchParamsObject.page && (
            // https://mui.com/x/react-data-grid/components/#pagination
            <DataGrid
              initialState={{
                sorting: {
                  sortModel: getSortModel(),
                },
              }}
              loading={refreshPage}
              rows={refreshPage ? [] : savServiceTodos ?? []}
              onSortModelChange={onSortModelChange}
              sortingMode="server"
              page={Number(searchParamsObject.page) - 1}
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={Number(searchParamsObject.itemsPerPage)}
              onPageSizeChange={onPageSizeChange}
              onPageChange={onPageChange}
              rowCount={totalItems}
              columns={columns}
              autoHeight={true}
              disableExtendRowFullWidth={true}
              localeText={getLocaleDataGrid(i18n.language)}
            />
          )}
        </Container>
      </Box>
    </>
  );
});

export default SavPrestationComponent;
